// frontend/src/components/QuantixGo/DeviceRequestsPage.jsx

import React, { useState, useEffect } from 'react';
import { quantixgoAPI } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { useTranslation } from '../../context/TranslationContext';
import DataTable from '../Common/DataTable';
import './DeviceRequestsPage.css';

const DeviceRequestsPage = () => {
  const { t } = useTranslation();
  const { toast_success, toast_error } = useToast();

  const [requests, setRequests] = useState([]);
  const [lookups, setLookups] = useState({ subsidiaries: [], salespersons: [], freeLicenses: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');

  const [approvalModal, setApprovalModal] = useState(null); // request object, or null
  const [form, setForm] = useState({
    device_code: '', subsidiary_code: '', salesperson_code: '',
    license_id: '', initial_password: '',
  });

  const loadRequests = async () => {
    try {
      setLoading(true);
      const res = await quantixgoAPI.listDeviceRequests(statusFilter === 'all' ? undefined : statusFilter);
      setRequests(res.data?.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading device requests:', err);
      setError(t('quantixgo.devices.loadError', 'Falha ao carregar pedidos'));
    } finally {
      setLoading(false);
    }
  };

  const loadLookups = async () => {
    try {
      const res = await quantixgoAPI.getDeviceApprovalLookups();
      setLookups(res.data?.data || { subsidiaries: [], salespersons: [], freeLicenses: [] });
    } catch (err) {
      console.error('Error loading lookups:', err);
    }
  };

  useEffect(() => { loadRequests(); }, [statusFilter]);
  useEffect(() => { loadLookups(); }, []);

  const filteredRequests = requests.filter((r) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();
    return (
      r.description?.toLowerCase().includes(term) ||
      r.device_fingerprint?.toLowerCase().includes(term) ||
      r.device_code?.toLowerCase().includes(term)
    );
  });

  const openApprovalModal = (request) => {
    setApprovalModal(request);
    setForm({ device_code: '', subsidiary_code: '', salesperson_code: '', license_id: '', initial_password: '' });
  };

  const selectedSalesperson = lookups.salespersons.find((sp) => sp.salesperson_code === form.salesperson_code);
  const salespersonsForSubsidiary = lookups.salespersons.filter((sp) => sp.subsidiary_code === form.subsidiary_code);

  const submitApproval = async () => {
      if (!form.device_code || !form.subsidiary_code || !form.salesperson_code) {
        toast_error(t('quantixgo.devices.missingFields', 'Código do dispositivo, subsidiária e vendedor são obrigatórios'));
        return;
      }
      if (selectedSalesperson?.needs_password && !form.initial_password) {
        toast_error(t('quantixgo.devices.passwordRequired', 'Este vendedor ainda não tem password — defina uma'));
        return;
      }
      if (!selectedSalesperson?.order_series_code && !form.order_series_code.trim()) {
        toast_error(t('quantixgo.devices.seriesRequired', 'Defina o código de série de numeração para este vendedor'));
        return;
      }

    try {
      await quantixgoAPI.approveDeviceRequest(approvalModal.device_request_id, form);
      toast_success(t('quantixgo.devices.approved', 'Dispositivo aprovado'));
      setApprovalModal(null);
      loadRequests();
      loadLookups();
    } catch (err) {
      console.error('Approval failed:', err);
      toast_error(err.response?.data?.error || t('quantixgo.devices.approveError', 'Falha ao aprovar'));
    }
  };

  const getStatusBadge = (request) => {
    if (request.approved_device_id) {
      return <span className="status-badge status-completed">✓ {t('quantixgo.devices.approved_status', 'Aprovado')}</span>;
    }
    return <span className="status-badge status-open">⭕ {t('quantixgo.devices.pending_status', 'Pendente')}</span>;
  };

  const columns = [
    {
      header: t('quantixgo.devices.description', 'Descrição'),
      field: 'description',
      render: (row) => row.description || <span className="text-muted">—</span>,
    },
    {
      header: t('quantixgo.devices.fingerprint', 'Fingerprint'),
      field: 'device_fingerprint',
      className: 'code-column',
    },
    {
      header: t('quantixgo.devices.requestedAt', 'Pedido em'),
      field: 'requested_at',
      render: (row) => new Date(row.requested_at).toLocaleString(),
    },
    {
      header: t('quantixgo.devices.deviceCodeCol', 'Dispositivo'),
      field: 'device_code',
      render: (row) => row.device_code || <span className="text-muted">—</span>,
    },
    {
      header: t('quantixgo.devices.salespersonCol', 'Vendedor'),
      field: 'salesperson_code',
      render: (row) => row.salesperson_code || <span className="text-muted">—</span>,
    },
    {
      header: t('common.status', 'Estado'),
      field: 'status',
      render: (row) => getStatusBadge(row),
    },
    {
      header: '',
      field: 'actions',
      className: 'text-right',
      render: (row) =>
        !row.approved_device_id && (
          <button className="btn-search" onClick={() => openApprovalModal(row)}>
            {t('quantixgo.devices.approve', 'Aprovar')}
          </button>
        ),
    },
  ];

  return (
    <div className="order-list device-requests-list">
      <div className="order-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>{t('common.search', 'Pesquisa')}</label>
            <input
              type="text"
              placeholder={t('quantixgo.devices.searchPlaceholder', 'Descrição, fingerprint, código...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>{t('common.status', 'Estado')}</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="pending">{t('quantixgo.devices.pending_status', 'Pendente')}</option>
              <option value="approved">{t('quantixgo.devices.approved_status', 'Aprovado')}</option>
              <option value="all">{t('common.all', 'Todos')}</option>
            </select>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredRequests}
        loading={loading}
        error={error}
        onRetry={loadRequests}
        emptyMessage={t('quantixgo.devices.none', 'Nenhum pedido encontrado')}
      />

      {approvalModal && (
        <div className="modal-overlay" onClick={() => setApprovalModal(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>{t('quantixgo.devices.approveTitle', 'Aprovar Dispositivo')}</h3>
            {approvalModal.description && (
              <p className="modal-subtitle">{approvalModal.description}</p>
            )}
            <p className="modal-fingerprint">{approvalModal.device_fingerprint}</p>

            <div className="form-group">
              <label>{t('quantixgo.devices.deviceCode', 'Código do dispositivo')}</label>
              <input
                value={form.device_code}
                onChange={(e) => setForm({ ...form, device_code: e.target.value })}
                placeholder="ex: 0034"
              />
            </div>

            <div className="form-group">
              <label>{t('quantixgo.devices.selectSubsidiary', 'Subsidiária')}</label>
              <select
                value={form.subsidiary_code}
                onChange={(e) => setForm({ ...form, subsidiary_code: e.target.value, salesperson_code: '' })}
              >
                <option value="">{t('common.select', 'Selecionar...')}</option>
                {lookups.subsidiaries.map((s) => (
                  <option key={s.subsidiary_code} value={s.subsidiary_code}>{s.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('quantixgo.devices.selectSalesperson', 'Vendedor')}</label>
              <select
                value={form.salesperson_code}
                onChange={(e) => setForm({ ...form, salesperson_code: e.target.value })}
                disabled={!form.subsidiary_code}
              >
                <option value="">{t('common.select', 'Selecionar...')}</option>
                {salespersonsForSubsidiary.map((sp) => (
                  <option key={sp.salesperson_code} value={sp.salesperson_code}>
                    {sp.name} {sp.needs_password ? `(${t('quantixgo.devices.noPassword', 'sem password')})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {selectedSalesperson && (
              <div className="form-group">
                <label>
                  {selectedSalesperson.needs_password
                    ? t('quantixgo.devices.setPassword', 'Definir password inicial')
                    : t('quantixgo.devices.optionalNewPassword', 'Nova password (opcional)')}
                </label>
                <input
                  type="password"
                  value={form.initial_password}
                  onChange={(e) => setForm({ ...form, initial_password: e.target.value })}
                />
              </div>
            )}


          {selectedSalesperson && !selectedSalesperson.order_series_code && (
            <div className="form-group">
              <label>
                {t('quantixgo.devices.orderSeriesCode', 'Código de série de numeração (novo vendedor)')} *
              </label>
              <input
                value={form.order_series_code}
              />
            </div>
          )}




            <div className="form-group">
              <label>{t('quantixgo.devices.selectLicense', 'Licença')}</label>
              <select value={form.license_id} onChange={(e) => setForm({ ...form, license_id: e.target.value })}>
                <option value="">{t('common.select', 'Selecionar...')}</option>
                {lookups.freeLicenses.map((l) => (
                  <option key={l.license_id} value={l.license_id}>#{l.license_id}</option>
                ))}
              </select>
              {lookups.freeLicenses.length === 0 && (
                <p className="form-warning">{t('quantixgo.devices.noLicenses', 'Sem licenças disponíveis')}</p>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-search" onClick={submitApproval}>
                {t('quantixgo.devices.confirm', 'Confirmar Aprovação')}
              </button>
              <button className="btn-secondary" onClick={() => setApprovalModal(null)}>
                {t('common.cancel', 'Cancelar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceRequestsPage;